import type { Dayjs } from 'dayjs';
import { type User } from '@features/profile/entity';
import dayjs from 'dayjs';
import { Service } from '../service/entity';
import { Client, ClientPersonalInfo, HasClientId, HasNote } from '../client/entity';
import {
  DatesFilter,
  getDate,
  getDay,
  getTimeFromUtcToLocal,
  VisitListApplyFilter,
} from '@shared/entity';
import { createVisitsColumns } from './components/visits-list/columns';
import { VisitsRowData } from './components/visits-list/visits-list.hooks';
import { Procedure, AddProcedureStockAllowanceType } from '@features/stock/procedure/entity';
import {
  getDayScheduleLabel,
  isDateHoliday,
  isWorkingDay,
  WorkingHoursScheduleType,
} from './schedule/entity';
import { getMissingStockAllowanceError } from '@features/stock/entity';

// ____BUDOUCÍ BE ÚPRAVA TYPY_____
export type VisitQueryDto = {
  client: Client;
  procedures: Procedure[];
  autor: Autor;
  user: User;
  loyaltyPointsUsed: number;
  loyaltyPointsGained: number;
  visitServices: Array<
    HasId & {
      service: Service;
    } & { serviceId: string } & HasVisitId
  >;
} & { visitStatus: boolean } & ClientPersonalInfo &
  HasNote &
  HasId & { dateTo: Date } & { date: Date } & HasClientId &
  VisitFinancials & { serviceIds: string } & HasUserId;

export type ClientVisitSummaryDto = { visitStatus: boolean } & {
  client: Client;
  user: User;
  autor: Autor;
  procedures: Procedure[];
  visitServices: Array<
    HasId & {
      service: Service;
      serviceId: string;
    } & HasVisitId
  >;
  loyaltyPointsUsed: number;
  loyaltyPointsGained: number;
  totalSalesRevenue?: number;
} & ClientPersonalInfo &
  HasNote &
  HasId & { dateTo: Date } & { date: Date } & HasClientId &
  VisitFinancials &
  HasUserId & { serviceIds: string } & {
    procedures: {
      id: string;
      visitId: string;
      stepOrder: number;
      description: string | null;
      stockAllowanceId: string | null;
      createdAt: Date;
      stockAllowances: AddProcedureStockAllowanceType;
    }[];
    totalSalesRevenue?: number;
  };

// ____REAFKTOROVANÝ TYP_____

export type VisitsByDateQueryParams = { date?: Dayjs; query?: { from?: Dayjs; to?: Dayjs } };

export type VisitDetail = {
  visit: VisitInfo;
  client: Client;
  services: Service[];
  visitServiceId: string;
  worker: User & { teamId: string };
  procedures: Procedure[];
  loyalty: VisitLoyalty;
  financials: VisitFinancials;
};

export enum Autor {
  CUSTOMER = 'CUSTOMER',
  ENTERPRENEUR = 'ENTERPRENEUR',
}

export type VisitInfo = HasId & { dateFrom: Date } & { dateTo: Date } & {
  visitStatus: boolean;
} & HasNote & { autor: Autor };

export type VisitLoyalty = {
  loyaltyPointsGained: number | undefined;
  loyaltyPointsUsed: number | undefined;
};

export type VisitFinancials = {
  deposit?: number;
  depositStatus?: DepositStatus;
  servicePaidStatus?: DepositStatus;
  productsPaidStatus?: DepositStatus;
  paidPrice?: number;
  totalSalesRevenue?: number;
};

// ____NE-REAFKTOROVANÝ TYP_____

export type HasId = { id: string };
export type HasVisitId = { visitId: string };
export type HasUserId = { userId: string };
export type HasTeamId = { teamId: string };

export type VisitForm = VisitFinancials & {
  date: Date;
  hairdresserId?: string;
  note: string | undefined;
  dateTo: Date;
  hairCutId?: string;
  visitServiceId: string;
  visitClosed: boolean;
  loyaltyPointsUsed?: number;
  loyaltyPointsGained?: number;
};

export type CreateVisitType = {
  deposit?: number;
  depositRequired: boolean;
  clientNote?: string;
  manualDateTimeOverride?: boolean;
  note: string | undefined;
  serviceIds: string;
  dateTo?: Date;
  date?: Date;
  clientId: string;
} & ClientPersonalInfo;

export type VisitViewKey = keyof typeof VisitView;
export enum VisitView {
  byAll = 'byAll',
  byClosedNoStockAllowances = 'byClosedNoStockAllowances',
}

export type DepositStatus = keyof typeof DepositStatus;
export const DepositStatus = {
  NEZAPLACENO: 'NEZAPLACENO',
  ZAPLACENO: 'ZAPLACENO',
} as const;

export type VisitRowHeader = {
  isHeader: true;
  isPlaceholder?: never;
  label: string;
  id: string;
};

export type VisitRowPlaceholder = {
  isPlaceholder: true;
  isHeader?: never;
  label: string;
  placeholderKind?: 'holiday' | 'nonWorking' | 'empty';
  id: string;
};

export type VisitRow =
  | VisitRowHeader
  | VisitRowPlaceholder
  | {
      id: string;
      date: string;
      dateTo: string;
      client: string;
      autor: Autor;
      serviceName: string;
      visitState: boolean;
      visitDepositPayed: boolean;
      clientDeposit: boolean;
      clientId: string;
      isHeader?: never;
      isPlaceholder?: never;
    };

export type OpenVisitsType = {
  rows: VisitRow[];
  columns: ReturnType<typeof createVisitsColumns>;
  isLoading: boolean;
  visits: VisitDetail[] | undefined;
};

export type MapReturnVisitFilter<T extends VisitListApplyFilter> =
  T extends 'dashBoardVisitOverView'
    ? [
        {
          dates: DatesFilter;
          view: VisitViewKey;
        },
        (updater: (draft: { dates: DatesFilter; view?: VisitViewKey }) => void) => void,
      ]
    : T extends 'allVisitsPage'
      ? [
          {
            dates: DatesFilter;
            view: VisitViewKey;
          },
          (updater: (draft: { dates: DatesFilter; view?: VisitViewKey }) => void) => void,
        ]
      : [
          {
            dates: DatesFilter;
            view: VisitViewKey;
          },
        ];

export const depositStatusOptions = Object.entries(DepositStatus).map(([key, value]) => ({
  id: key,
  name: value,
}));

export const getVisitUrlComposed = (date?: Dayjs, query?: { from?: Dayjs; to?: Dayjs }) => {
  const params = new URLSearchParams();

  if (date) {
    if (!date.isValid()) {
      throw new Error('Neplatný filtr data návštěv.');
    }

    params.append('date', date.toISOString());
    return `/api/visits?${params.toString()}`;
  }

  if (query) {
    if (!query.from || !query.to || !query.from.isValid() || !query.to.isValid()) {
      throw new Error('Neplatný rozsah data návštěv.');
    }

    params.append('from', query.from.format('YYYY-MM-DD'));
    params.append('to', query.to.format('YYYY-MM-DD'));

    return `/api/visits?${params.toString()}`;
  }

  return '/api/visits';
};

export const findPreviousVisitWithSameService = (
  clientVisits: VisitDetail[],
  currentVisitId: string,
  currentServiceIds: string,
) => {
  const previousVisits = clientVisits.filter(
    visit => visit.procedures.length && visit.visit.id !== currentVisitId,
  );

  const sortedPreviousVisits = previousVisits.sort(
    (a, b) => dayjs(b.visit.dateFrom).valueOf() - dayjs(a.visit.dateFrom).valueOf(),
  );

  const previousVisitWithSameService = sortedPreviousVisits.find(visit =>
    visit.services.some(service => service.id === currentServiceIds),
  );

  return previousVisitWithSameService ?? null;
};

export const getVisitFinishErrors = (
  clientDeposit: boolean,
  watchFormVisitData: {
    paidPrice: number | undefined;
    deposit: number | undefined;
    depositStatus: DepositStatus | null | undefined;
  },
  t: (key: string) => string,
): string[] => {
  const { paidPrice, deposit, depositStatus } = watchFormVisitData;
  const isDepositRequired = clientDeposit;
  const errors: string[] = [];

  if (!paidPrice || paidPrice <= 0) {
    errors.push(t('visitDetailGrid.paidPriceWarning'));
  }

  if (isDepositRequired) {
    if (!deposit || deposit <= 0) {
      errors.push(t('visitDetailGrid.depositStatusWarning'));
    }
    if (depositStatus !== DepositStatus.ZAPLACENO) {
      errors.push(t('visitDetailGrid.depositWarning'));
    }
  }

  return errors;
};

export const getVistsRows = (
  visitData: VisitDetail[] | undefined,
  view: VisitViewKey,
  t: (key: string) => string,
  options?: {
    workSchedule?: WorkingHoursScheduleType;
    dateRange?: { from: Dayjs; to: Dayjs };
  },
) => {
  if ((!visitData || visitData.length === 0) && !(options?.workSchedule && options?.dateRange)) {
    return [];
  }

  const safeVisitData = visitData ?? [];

  const onlyClosedVisitsWithoutStockAllowances = safeVisitData.filter(
    ({ visit, procedures }) => visit.visitStatus && getMissingStockAllowanceError(t, procedures),
  );

  const filteredData =
    view === 'byClosedNoStockAllowances' ? onlyClosedVisitsWithoutStockAllowances : safeVisitData;

  const sortedVisits = [...filteredData].sort(
    (a, b) => dayjs(a.visit.dateFrom).valueOf() - dayjs(b.visit.dateFrom).valueOf(),
  );

  return getRowsWithHeaders(sortedVisits, t, options?.workSchedule, options?.dateRange);
};

export const getVisitsByDateQueryKey = (params: VisitsByDateQueryParams) =>
  params?.query
    ? [
        'visits',
        dayjs(params.query?.from).format('YYYY-MM-DD'),
        dayjs(params.query?.to).format('YYYY-MM-DD'),
      ]
    : params?.date
      ? ['visits', dayjs(params.date).format('YYYY-MM-DD')]
      : ['visits'];

export const getRowsWithHeaders = (
  visits: VisitDetail[],
  t: (key: string) => string,
  workSchedule?: WorkingHoursScheduleType,
  dateRange?: { from: Dayjs; to: Dayjs },
): VisitsRowData[] => {
  const rows: VisitsRowData[] = [];

  if (workSchedule && dateRange) {
    let currentDay = dateRange.from.startOf('day');
    const endDay = dateRange.to.startOf('day');

    while (currentDay.valueOf() <= endDay.valueOf()) {
      const dayStr = currentDay.format('YYYY-MM-DD');
      const dayVisits = visits.filter(
        ({ visit }) => dayjs(new Date(visit.dateFrom)).format('YYYY-MM-DD') === dayStr,
      );
      const dayIsHoliday = isDateHoliday(currentDay, workSchedule);
      const dayIsWorking = isWorkingDay(currentDay, workSchedule);

      rows.push({
        id: `header-${dayStr}`,
        isHeader: true,
        label:
          `${getDay(currentDay.toDate())} - ${getDate(currentDay.toDate())}` +
          getDayScheduleLabel(currentDay, workSchedule),
      });

      if (dayVisits.length > 0) {
        const mapVisits = dayVisits.map(({ visit, client, services, financials }) => ({
          id: visit.id,
          date: getTimeFromUtcToLocal(visit.dateFrom),
          dateTo: getTimeFromUtcToLocal(visit.dateTo),
          client: `${client.firstName} ${client.lastName}`,
          serviceName: services.map(service => service.serviceName).join(','),
          visitDepositPayed: financials.depositStatus === DepositStatus.ZAPLACENO,
          visitState: visit.visitStatus,
          autor: visit.autor,
          clientId: client.id,
          clientDeposit: client.deposit,
        }));

        rows.push(...mapVisits);
      } else {
        rows.push({
          id: `placeholder-${dayStr}`,
          isPlaceholder: true,
          placeholderKind: dayIsHoliday ? 'holiday' : dayIsWorking ? 'empty' : 'nonWorking',
          label: dayIsHoliday
            ? 'Dovolená'
            : dayIsWorking
              ? 'Zatím žádná návštěva'
              : t('dashboard.notWorkingDay'),
        });
      }

      currentDay = currentDay.add(1, 'day');
    }

    return rows;
  }

  let lastDate: Date | null = null;

  for (const visitDetail of visits) {
    const { visit, client, services, financials } = visitDetail;
    const visitDay = getDate(visit.dateFrom);
    const lastDay = lastDate ? getDate(lastDate) : null;

    if (visitDay !== lastDay) {
      rows.push({
        id: `header-${visit.dateFrom}`,
        isHeader: true,
        label: 'Den - ' + `${getDate(visit.dateFrom)} - ${getDay(visit.dateFrom)}`,
      });

      lastDate = visit.dateFrom;
    }

    rows.push({
      id: visit.id,
      date: getTimeFromUtcToLocal(visit.dateFrom),
      dateTo: getTimeFromUtcToLocal(visit.dateTo),
      client: `${client.firstName} ${client.lastName}`,
      serviceName: services.map(service => service.serviceName).join(','),
      visitDepositPayed: financials.depositStatus === DepositStatus.ZAPLACENO,
      visitState: visit.visitStatus,
      autor: visit.autor,
      clientId: client.id,
      clientDeposit: client.deposit,
    });
  }

  return rows;
};

export const getTimeOfDayAvailability = (
  slots: { start: string; end: string }[],
): 'morning' | 'afternoon' | 'both' | null => {
  let hasMorning = false;
  let hasAfternoon = false;

  for (const slot of slots) {
    const startHour = parseInt(slot.start.split(':')[0]);
    const endHour = parseInt(slot.end.split(':')[0]);
    const endMinutes = parseInt(slot.end.split(':')[1]);

    if (startHour < 12) {
      hasMorning = true;
    }
    if (endHour > 12 || (endHour === 12 && endMinutes > 0)) {
      hasAfternoon = true;
    }

    if (hasMorning && hasAfternoon) {
      return 'both';
    }
  }

  if (hasMorning) {
    return 'morning';
  }
  if (hasAfternoon) {
    return 'afternoon';
  }
  return null;
};

export const VISIT_FILTER_BUTTONS_CONFIG: Record<
  VisitViewKey,
  { defaultMessage: string; id: string }
> = {
  [VisitView.byAll]: { defaultMessage: 'Všechny', id: 'visits.visitViewKey.byAll' },
  [VisitView.byClosedNoStockAllowances]: {
    defaultMessage: 'Uzavřené bez spotřeby',
    id: 'visits.visitViewKey.byClosedNoStockAllowances',
  },
};

export const DASHBOARD_TABLE_CONFIG = {
  columnHeaderHeight: 0,
  hideFooter: true,
  disableColumnMenu: true,
  disableRowSelectionOnClick: true,
} as const;

export const FULL_PAGE_TABLE_CONFIG = {
  columnHeaderHeight: 56,
  hideFooter: false,
  disableColumnMenu: false,
  disableRowSelectionOnClick: true,
} as const;
